import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPlans } from "../../helpers/backend_helper";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import HomeLogoLink from "../../Components/Common/HomeLogoLink";
import { formatExpiredAt, getExpiryStatus } from "../../helpers/helpers";
import { sanitizeHtml } from "../../helpers/sanitizeHtml";

interface PlanItem {
    id: number;
    name: string;
    slug: string;
    description?: string | null;
    monthly_price?: number | null;
    is_current?: boolean;
}

const formatMonthlyPrice = (value?: number | null): string => {
    if (value === null || value === undefined) {
        return "--";
    }

    return `${new Intl.NumberFormat("vi-VN").format(value)}đ/tháng`;
};

const SubscriptionPage = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState<PlanItem[]>([]);
    const [currentPlanId, setCurrentPlanId] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [currentPlanExpiredAt, setCurrentPlanExpiredAt] = useState<string | null>(null);

    useEffect(() => {
        const fetchPlans = async () => {
            setLoading(true);
            setError("");

            try {
                const response: any = await getPlans();
                const payload = response?.data?.data ?? response?.data ?? {};
                setCurrentPlanId(payload?.current_plan_id ?? null);
                setCurrentPlanExpiredAt(payload?.current_plan_expired_at ?? null);
                setPlans(Array.isArray(payload?.plans) ? payload.plans : []);
            } catch (err: any) {
                setError(err?.message || "Không thể tải danh sách gói.");
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, []);

    const allPlans = useMemo(() => {
        return plans.map((plan) => ({
            ...plan,
            is_current: plan.is_current ?? (currentPlanId !== null && plan.id === currentPlanId),
        }));
    }, [plans, currentPlanId]);

    return (
        <React.Fragment>
            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <HomeLogoLink className="me-2" />

                    <div className="page-heading">
                        <h6 className="mb-0">Đăng ký gói</h6>
                    </div>

                    <HeaderSidebarToggle className="ms-2" />
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container py-3">
                    {loading && <p className="text-center mb-0">Đang tải danh sách gói...</p>}
                    {!loading && error && <p className="text-danger text-center mb-0">{error}</p>}

                    <div className="row g-3 mt-1">
                        {allPlans.map((plan) => {
                            const isCurrent = Boolean(plan.is_current);
                            const expiryStatus = isCurrent ? getExpiryStatus(currentPlanExpiredAt) : null;

                            return (
                                <div key={plan.id} className="col-12">
                                    <div className="card h-100">
                                        <div className="card-body position-relative">
                                            <h5 className="mb-2">{plan.name}</h5>
                                            <p className="text-primary fw-semibold mb-3">
                                                {formatMonthlyPrice(plan.monthly_price)}
                                            </p>

                                            <button
                                                type="button"
                                                className={`btn w-100 ${isCurrent ? "btn-secondary" : "btn-primary"}`}
                                                disabled={isCurrent}
                                                onClick={() => navigate(`/subscription/${plan.id}`)}
                                            >
                                                {isCurrent ? "Gói hiện tại của bạn" : "Đăng ký gói"}
                                            </button>

                                            {isCurrent && (
                                                <>
                                                    <span
                                                        className={`position-absolute top-1rem right-1rem badge rounded-pill ${
                                                            expiryStatus === "danger"
                                                                ? "bg-danger"
                                                                : expiryStatus === "warning"
                                                                  ? "bg-warning text-dark"
                                                                  : "bg-success"
                                                        }`}
                                                    >
                                                        Hiệu lực đến: {formatExpiredAt(currentPlanExpiredAt)}
                                                    </span>
                                                </>
                                            )}

                                            <div className="mt-3">
                                                <div
                                                    className="text-muted"
                                                    dangerouslySetInnerHTML={{
                                                        __html: sanitizeHtml(plan.description || "<p>Chưa có mô tả cho gói này.</p>"),
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default SubscriptionPage;
